import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  type CreateInterestPhaseInput,
  type CreateLoanInput,
  type CreateOneTimeEntryInput,
  type CreatePrepaymentInput,
  type CreatePropertyInput,
  type CreateRecurringCostInput,
  type CreateRentChangeInput,
  type CreateTenantInput,
  type CreateVacancyAnnotationInput,
  type CreateValuationInput,
  type ListPropertiesFilters,
  type UpdateInterestPhaseInput,
  type UpdateLoanInput,
  type UpdateOneTimeEntryInput,
  type UpdatePrepaymentInput,
  type UpdatePropertyInput,
  type UpdateRecurringCostInput,
  type UpdateRentChangeInput,
  type UpdateTenantInput,
  type UpdateVacancyAnnotationInput,
  type UpdateValuationInput,
  type UpsertScheduleOverrideInput,
  realestateApi,
  vacancyApi,
} from "../lib/realestate";

const baseKey = ["realestate"] as const;

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: baseKey });
  qc.invalidateQueries({ queryKey: ["networth"] });
}

export function useProperties(filters: ListPropertiesFilters = {}) {
  return useQuery({
    queryKey: [...baseKey, "list", filters],
    queryFn: () => realestateApi.list(filters),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useProperty(id: string) {
  return useQuery({
    queryKey: [...baseKey, "detail", id],
    queryFn: () => realestateApi.get(id),
    enabled: Boolean(id),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function usePortfolioSummary() {
  return useQuery({
    queryKey: [...baseKey, "summary"],
    queryFn: () => realestateApi.summary(),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useAmortizationSchedule(propertyId: string, loanId: string) {
  return useQuery({
    queryKey: [...baseKey, "schedule", propertyId, loanId],
    queryFn: () => realestateApi.schedule(propertyId, loanId),
    enabled: Boolean(propertyId && loanId),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useCashflow(propertyId: string, year: number) {
  return useQuery({
    queryKey: [...baseKey, "cashflow", propertyId, year],
    queryFn: () => realestateApi.cashflow(propertyId, year),
    enabled: Boolean(propertyId),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useYields(propertyId: string, year: number) {
  return useQuery({
    queryKey: [...baseKey, "yields", propertyId, year],
    queryFn: () => realestateApi.yields(propertyId, year),
    enabled: Boolean(propertyId),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useCashflowOverview(propertyId: string) {
  return useQuery({
    queryKey: [...baseKey, "cashflow-overview", propertyId],
    queryFn: () => realestateApi.cashflowOverview(propertyId),
    enabled: Boolean(propertyId),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useAfa(propertyId: string, asOf?: string) {
  return useQuery({
    queryKey: [...baseKey, "afa", propertyId, asOf ?? "now"],
    queryFn: () => realestateApi.afa(propertyId, asOf),
    enabled: Boolean(propertyId),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useEquity(propertyId: string, asOf?: string) {
  return useQuery({
    queryKey: [...baseKey, "equity", propertyId, asOf ?? "now"],
    queryFn: () => realestateApi.equity(propertyId, asOf),
    enabled: Boolean(propertyId),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useCreateProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePropertyInput) => realestateApi.create(input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdatePropertyInput }) =>
      realestateApi.update(id, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useSoftDeleteProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => realestateApi.softDelete(id),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useHardDeleteProperty() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => realestateApi.hardDelete(id),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useAddLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ propertyId, input }: { propertyId: string; input: CreateLoanInput }) =>
      realestateApi.addLoan(propertyId, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      loanId,
      input,
    }: { propertyId: string; loanId: string; input: UpdateLoanInput }) =>
      realestateApi.updateLoan(propertyId, loanId, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useHardDeleteLoan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ propertyId, loanId }: { propertyId: string; loanId: string }) =>
      realestateApi.hardDeleteLoan(propertyId, loanId),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useAddPrepayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      loanId,
      input,
    }: { propertyId: string; loanId: string; input: CreatePrepaymentInput }) =>
      realestateApi.addPrepayment(propertyId, loanId, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeletePrepayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      loanId,
      prepaymentId,
    }: { propertyId: string; loanId: string; prepaymentId: string }) =>
      realestateApi.deletePrepayment(propertyId, loanId, prepaymentId),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useAddValuation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ propertyId, input }: { propertyId: string; input: CreateValuationInput }) =>
      realestateApi.addValuation(propertyId, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteValuation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ propertyId, valuationId }: { propertyId: string; valuationId: string }) =>
      realestateApi.deleteValuation(propertyId, valuationId),
    onSuccess: () => invalidateAll(qc),
  });
}

// ---- Tenants ----

export function useTenants(propertyId: string, options?: { active?: boolean }) {
  return useQuery({
    queryKey: [...baseKey, "tenants", propertyId, options?.active ?? "all"],
    queryFn: () => realestateApi.listTenants(propertyId, options),
    enabled: Boolean(propertyId),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ propertyId, input }: { propertyId: string; input: CreateTenantInput }) =>
      realestateApi.addTenant(propertyId, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      tenantId,
      input,
    }: { propertyId: string; tenantId: string; input: UpdateTenantInput }) =>
      realestateApi.updateTenant(propertyId, tenantId, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useEndTenancy() {
  // UI-Wrapper: setzt moveOutDate=heute via updateTenant. Kein eigener Endpoint.
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ propertyId, tenantId }: { propertyId: string; tenantId: string }) => {
      const today = new Date().toISOString().slice(0, 10);
      return realestateApi.updateTenant(propertyId, tenantId, { moveOutDate: today });
    },
    onSuccess: () => invalidateAll(qc),
  });
}

export function useHardDeleteTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ propertyId, tenantId }: { propertyId: string; tenantId: string }) =>
      realestateApi.hardDeleteTenant(propertyId, tenantId),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useAddRentChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      tenantId,
      input,
    }: { propertyId: string; tenantId: string; input: CreateRentChangeInput }) =>
      realestateApi.addRentChange(propertyId, tenantId, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteRentChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      tenantId,
      rentChangeId,
    }: { propertyId: string; tenantId: string; rentChangeId: string }) =>
      realestateApi.deleteRentChange(propertyId, tenantId, rentChangeId),
    onSuccess: () => invalidateAll(qc),
  });
}

// ---- RecurringCosts ----

export function useRecurringCosts(propertyId: string) {
  return useQuery({
    queryKey: [...baseKey, "recurringCosts", propertyId],
    queryFn: () => realestateApi.listRecurringCosts(propertyId),
    enabled: Boolean(propertyId),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useAddRecurringCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ propertyId, input }: { propertyId: string; input: CreateRecurringCostInput }) =>
      realestateApi.addRecurringCost(propertyId, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteRecurringCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ propertyId, costId }: { propertyId: string; costId: string }) =>
      realestateApi.deleteRecurringCost(propertyId, costId),
    onSuccess: () => invalidateAll(qc),
  });
}

// ---- OneTimeEntries ----

export function useOneTimeEntries(propertyId: string, query?: { from?: string; to?: string }) {
  return useQuery({
    queryKey: [...baseKey, "oneTimeEntries", propertyId, query?.from ?? "", query?.to ?? ""],
    queryFn: () => realestateApi.listOneTimeEntries(propertyId, query),
    enabled: Boolean(propertyId),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useAddOneTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ propertyId, input }: { propertyId: string; input: CreateOneTimeEntryInput }) =>
      realestateApi.addOneTimeEntry(propertyId, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteOneTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ propertyId, entryId }: { propertyId: string; entryId: string }) =>
      realestateApi.deleteOneTimeEntry(propertyId, entryId),
    onSuccess: () => invalidateAll(qc),
  });
}

// ---- Schedule-Overrides ----

export function useUpsertScheduleOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      loanId,
      input,
    }: {
      propertyId: string;
      loanId: string;
      input: UpsertScheduleOverrideInput;
    }) => realestateApi.upsertScheduleOverride(propertyId, loanId, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: [...baseKey, "schedule", vars.propertyId, vars.loanId],
      });
      qc.invalidateQueries({ queryKey: [...baseKey, "detail", vars.propertyId] });
      qc.invalidateQueries({ queryKey: ["allocation"] });
      qc.invalidateQueries({ queryKey: ["networth"] });
    },
  });
}

export function useDeleteScheduleOverride() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      loanId,
      overrideId,
    }: {
      propertyId: string;
      loanId: string;
      overrideId: string;
    }) => realestateApi.deleteScheduleOverride(propertyId, loanId, overrideId),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: [...baseKey, "schedule", vars.propertyId, vars.loanId],
      });
      qc.invalidateQueries({ queryKey: [...baseKey, "detail", vars.propertyId] });
      qc.invalidateQueries({ queryKey: ["allocation"] });
      qc.invalidateQueries({ queryKey: ["networth"] });
    },
  });
}

// ---- Vacancy ----

export function useVacancies(propertyId: string, asOf?: string) {
  return useQuery({
    queryKey: [...baseKey, "vacancies", propertyId, asOf ?? "today"],
    queryFn: () => vacancyApi.list(propertyId, asOf),
    enabled: Boolean(propertyId),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useVacancyStatistics(propertyId: string, year: number, asOf?: string) {
  return useQuery({
    queryKey: [...baseKey, "vacancy-stats", propertyId, year, asOf ?? "today"],
    queryFn: () => vacancyApi.statistics(propertyId, year, asOf),
    enabled: Boolean(propertyId),
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useCreateVacancyAnnotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      input,
    }: {
      propertyId: string;
      input: CreateVacancyAnnotationInput;
    }) => vacancyApi.create(propertyId, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useUpdateVacancyAnnotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      annotationId,
      input,
    }: {
      annotationId: string;
      input: UpdateVacancyAnnotationInput;
    }) => vacancyApi.update(annotationId, input),
    onSuccess: () => invalidateAll(qc),
  });
}

export function useDeleteVacancyAnnotation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (annotationId: string) => vacancyApi.remove(annotationId),
    onSuccess: () => invalidateAll(qc),
  });
}

// ---- Interest-Phases ----
// Hinweis: Kein useInterestPhases-Hook — Phasen werden ueber loan.interestPhases
// aus dem getProperty-DTO gelesen. Bei Bedarf (z.B. Lazy-Load fuer eine
// dedizierte Phasen-View) hier hinzufuegen, dann analog zu useVacancies.

export function useAddInterestPhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      loanId,
      input,
    }: { propertyId: string; loanId: string; input: CreateInterestPhaseInput }) =>
      realestateApi.addInterestPhase(propertyId, loanId, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: [...baseKey, "interestPhases", vars.propertyId, vars.loanId],
      });
      qc.invalidateQueries({ queryKey: [...baseKey, "schedule", vars.propertyId, vars.loanId] });
      qc.invalidateQueries({ queryKey: [...baseKey, "detail", vars.propertyId] });
      qc.invalidateQueries({ queryKey: ["networth"] });
    },
  });
}

export function useUpdateInterestPhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      loanId,
      phaseId,
      input,
    }: {
      propertyId: string;
      loanId: string;
      phaseId: string;
      input: UpdateInterestPhaseInput;
    }) => realestateApi.updateInterestPhase(propertyId, loanId, phaseId, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: [...baseKey, "interestPhases", vars.propertyId, vars.loanId],
      });
      qc.invalidateQueries({ queryKey: [...baseKey, "schedule", vars.propertyId, vars.loanId] });
      qc.invalidateQueries({ queryKey: [...baseKey, "detail", vars.propertyId] });
      qc.invalidateQueries({ queryKey: ["networth"] });
    },
  });
}

export function useDeleteInterestPhase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      loanId,
      phaseId,
    }: { propertyId: string; loanId: string; phaseId: string }) =>
      realestateApi.deleteInterestPhase(propertyId, loanId, phaseId),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({
        queryKey: [...baseKey, "interestPhases", vars.propertyId, vars.loanId],
      });
      qc.invalidateQueries({ queryKey: [...baseKey, "schedule", vars.propertyId, vars.loanId] });
      qc.invalidateQueries({ queryKey: [...baseKey, "detail", vars.propertyId] });
      qc.invalidateQueries({ queryKey: ["networth"] });
    },
  });
}

// ---- Historical-Edit PATCH-Hooks ----

export function useUpdateRentChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      tenantId,
      rentChangeId,
      input,
    }: {
      propertyId: string;
      tenantId: string;
      rentChangeId: string;
      input: UpdateRentChangeInput;
    }) => realestateApi.updateRentChange(propertyId, tenantId, rentChangeId, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [...baseKey, "detail", vars.propertyId] });
      qc.invalidateQueries({ queryKey: ["networth"] });
    },
  });
}

export function useUpdatePrepayment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      loanId,
      prepaymentId,
      input,
    }: {
      propertyId: string;
      loanId: string;
      prepaymentId: string;
      input: UpdatePrepaymentInput;
    }) => realestateApi.updatePrepayment(propertyId, loanId, prepaymentId, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [...baseKey, "schedule", vars.propertyId, vars.loanId] });
      qc.invalidateQueries({ queryKey: [...baseKey, "detail", vars.propertyId] });
      qc.invalidateQueries({ queryKey: ["networth"] });
    },
  });
}

export function useUpdateValuation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      valuationId,
      input,
    }: { propertyId: string; valuationId: string; input: UpdateValuationInput }) =>
      realestateApi.updateValuation(propertyId, valuationId, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [...baseKey, "detail", vars.propertyId] });
      qc.invalidateQueries({ queryKey: ["networth"] });
    },
  });
}

export function useUpdateRecurringCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      costId,
      input,
    }: { propertyId: string; costId: string; input: UpdateRecurringCostInput }) =>
      realestateApi.updateRecurringCost(propertyId, costId, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [...baseKey, "detail", vars.propertyId] });
      qc.invalidateQueries({ queryKey: ["networth"] });
    },
  });
}

export function useUpdateOneTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      propertyId,
      entryId,
      input,
    }: { propertyId: string; entryId: string; input: UpdateOneTimeEntryInput }) =>
      realestateApi.updateOneTimeEntry(propertyId, entryId, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: [...baseKey, "detail", vars.propertyId] });
      qc.invalidateQueries({ queryKey: ["networth"] });
    },
  });
}
